import { useState, useEffect, useRef } from 'react'

interface RateModificationModalProps {
  isOpen: boolean
  currentRate: number
  currencyCode: string
  tradeType: 'buy' | 'sell'
  onConfirm: (newRate: number, reason: string) => void
  onCancel: () => void
}

function RateModificationModal({
  isOpen,
  currentRate,
  currencyCode,
  tradeType,
  onConfirm,
  onCancel,
}: RateModificationModalProps) {
  const [newRate, setNewRate] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const rateInputRef = useRef<HTMLInputElement>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewRate(currentRate.toString())
      setReason('')
      setError(null)
      // 모달이 열릴 때 환율 입력 필드에 포커스
      setTimeout(() => {
        rateInputRef.current?.focus()
        rateInputRef.current?.select()
      }, 100)
    }
  }, [isOpen, currentRate])

  if (!isOpen) return null

  const handleConfirm = () => {
    const rateNum = parseFloat(newRate)

    if (Number.isNaN(rateNum) || rateNum <= 0) {
      setError('유효한 환율을 입력해주세요.')
      return
    }

    if (!reason.trim()) {
      setError('변경 사유를 입력해주세요.')
      return
    }

    onConfirm(rateNum, reason.trim())
  }

  const handleCancel = () => {
    setNewRate('')
    setReason('')
    setError(null)
    onCancel()
  }

  const handleReset = () => {
    setNewRate(currentRate.toString())
    setReason('')
    setError(null)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-fx-text-dark">
            {tradeType === 'buy' ? '매입' : '매각'} 환율 수정
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Current Rate (read-only) */}
          <div>
            <label className="block text-sm font-medium text-fx-text-muted mb-2">
              현재 기본 환율 ({currencyCode})
            </label>
            <div className="px-4 py-3 bg-gray-100 rounded-lg text-lg font-semibold text-fx-text-dark">
              {currentRate.toLocaleString()} 원
            </div>
          </div>

          {/* New Rate (input) */}
          <div>
            <label className="block text-sm font-medium text-fx-text-muted mb-2">
              적용할 환율
            </label>
            <input
              ref={rateInputRef}
              type="number"
              value={newRate}
              onChange={(e) => {
                setNewRate(e.target.value)
                setError(null)
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fx-buy focus:border-transparent text-lg"
              placeholder="0"
              step="0.01"
              autoFocus
            />
          </div>

          {/* Reason (required) */}
          <div>
            <label className="block text-sm font-medium text-fx-text-muted mb-2">
              변경 사유 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                setError(null)
              }}
              placeholder="예: 고객 요청, 시장 변동 반영 등"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fx-buy focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={handleReset}
            className="px-6 py-2 border border-gray-300 rounded-lg text-fx-text-dark font-medium hover:bg-gray-50 transition-all"
          >
            초기화
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-fx-confirm text-white rounded-lg font-medium hover:opacity-90 transition-all"
          >
            적용하기
          </button>
        </div>
      </div>
    </div>
  )
}

export default RateModificationModal
